import { useCallback, useRef, useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Pagination, PaginationLink } from '~services/pagination';
import { getPageNumber } from '~utils/pagination';

interface DeletedRecordsCursor {
  cursor?: string;
  direction?: PaginationLink;
  page: number;
}

const FIRST_PAGE: DeletedRecordsCursor = { page: 1 };

export default function useDeletedRecordsCursor(filtersKey: string) {
  const [cursor, setCursor] = useState<DeletedRecordsCursor>(FIRST_PAGE);

  const previousFiltersKeyRef = useRef(filtersKey);
  const filtersChanged = previousFiltersKeyRef.current !== filtersKey;

  // Reset during render: an effect runs too late to stop the render that saw the new
  // filters from querying with the previous page's cursor.
  if (filtersChanged) {
    previousFiltersKeyRef.current = filtersKey;
    setCursor(FIRST_PAGE);
  }

  const effectiveCursor = filtersChanged ? FIRST_PAGE : cursor;

  // DataGridPager sends no direction for its first-page jump, which on a cursor API
  // means sending no cursor at all.
  const goToPage = useCallback((direction?: PaginationLink, pagination?: Pagination) => {
    if (!direction) {
      setCursor(FIRST_PAGE);

      return;
    }

    const nextCursor = pagination?.[direction];

    if (!nextCursor) {
      return;
    }

    setCursor((state) => ({
      cursor: nextCursor,
      direction,
      page: getPageNumber(state.page, direction),
    }));
  }, []);

  // extractPagination reports only the cursors, so page and limit come from here.
  const paginationForDisplay = useCallback(
    (pagination?: Pagination) => ({
      after: pagination?.after,
      before: pagination?.before,
      limit: MAX_API_PAGE_SIZE,
      page: effectiveCursor.page,
    }),
    [effectiveCursor.page],
  );

  return { cursor: effectiveCursor, goToPage, paginationForDisplay };
}
